import React from 'react';
import { Loader2, Utensils, Search, Send, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { moduleApi } from '../../../lib/api';
import { dishStatusFor, removalSuggestionsFor } from '../../../../shared/dishCompatibility.js';

interface GuestDetail {
    id: string;
    name: string;
    email: string;
    table_number: string | null;
    responded_at: string | null;
    dishes_by_category: Record<string, string>;
    restrictions: string[];
    allergies: string[];
    medical_note: string;
    menu_skipped_categories: string[];
    /** Catégories où le convive a explicitement demandé qu'on lui propose autre chose. */
    menu_alt_requested?: string[];
}

interface CatalogIngredient { name: string; removable: boolean; components?: string[]; }
interface CatalogDish { id: string; name: string; category: string; description: string; ingredients: CatalogIngredient[]; dietary_tags?: string[]; }

/** Picker de plats de secours pour UNE catégorie où le convive a déclaré "je ne prendrai rien" — puise dans
 *  TOUT le catalogue (toutes formules confondues), pas seulement la carte assignée à l'événement. Chaque
 *  plat est badgé selon les allergies déjà déclarées par le convive, pour guider le choix d'Emeni sans
 *  qu'elle ait à recroiser manuellement. Demande explicite d'Emeni (2026-08-25). */
function AlternativeDishPicker({ eventId, guestId, category, allergies, restrictions, onSent, onCancel }: {
    eventId: string; guestId: string; category: string; allergies: string[]; restrictions: string[];
    onSent: () => void; onCancel: () => void;
}) {
    const [loading, setLoading] = React.useState(true);
    const [dishes, setDishes] = React.useState<CatalogDish[]>([]);
    const [allergenTerms, setAllergenTerms] = React.useState<any[] | null>(null);
    const [search, setSearch] = React.useState('');
    const [selected, setSelected] = React.useState<string[]>([]);
    const [sending, setSending] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        // Le référentiel de termes personnalisés est indispensable : sans lui, un établissement qui a déclaré
        // que « béchamel » contient du lactose verrait ce plat passé pour compatible.
        Promise.all([moduleApi.getMenuItems(), moduleApi.getAllergenTerms()])
            .then(([items, terms]: any[]) => {
                setDishes((Array.isArray(items) ? items : []).filter((d: CatalogDish) => d.category === category));
                setAllergenTerms(Array.isArray(terms) ? terms : []);
            })
            .catch(() => { setDishes([]); setAllergenTerms([]); })
            .finally(() => setLoading(false));
    }, [category]);

    // Le profil complet du convive : restrictions ET allergies. La version précédente ne regardait que les
    // allergies — on pouvait donc proposer un plat au beurre à quelqu'un « sans lactose », ou du porc à
    // quelqu'un qui n'en mange pas. Demande d'Emeni (2026-08-24). On réutilise dishCompatibility, la même
    // règle que celle appliquée au convive sur sa propre page : une alternative jugée bonne ici ne peut donc
    // pas être refusée par son écran à l'arrivée.
    const profile = React.useMemo(
        () => ({ restrictions, allergies, customTerms: allergenTerms || [] }),
        [restrictions, allergies, allergenTerms],
    );

    const searched = dishes.filter((d) => !search.trim() || d.name.toLowerCase().includes(search.trim().toLowerCase()));
    // 'ok' convient tel quel, 'adaptable' convient si la cuisine retire ce qui gêne, 'bloque' hors de portée.
    const rated = searched.map((d) => ({
        dish: d,
        status: dishStatusFor(d, profile) as 'ok' | 'adaptable' | 'bloque',
        removals: removalSuggestionsFor(d, profile),
    }));
    // Les plats proposés d'abord, les impossibles relégués en fin de liste et non sélectionnables : les
    // masquer complètement laisserait croire à une carte vide sans jamais dire pourquoi.
    const proposable = rated.filter((r) => r.status !== 'bloque');
    const blocked = rated.filter((r) => r.status === 'bloque');

    const toggle = (id: string) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

    const send = async () => {
        if (!selected.length) return;
        setSending(true);
        setError('');
        try {
            const res: any = await moduleApi.offerAlternativeDish(eventId, guestId, category, selected);
            if (res?.error) { setError(res.error); return; }
            onSent();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'envoi.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="rounded-xl border border-border bg-surface p-3 space-y-2.5">
            <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-content">Proposer un plat alternatif — {category}</p>
                <button onClick={onCancel} className="text-content-muted hover:text-content"><X size={14} /></button>
            </div>
            {loading ? (
                <div className="flex justify-center py-4"><Loader2 className="animate-spin text-content-muted" size={18} /></div>
            ) : (
                <>
                    <div className="relative">
                        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un plat..."
                            className="w-full pl-7 pr-3 py-1.5 rounded-lg bg-surface-soft border border-border text-xs text-content outline-none focus:border-brand-500" />
                    </div>
                    <div className="max-h-52 overflow-y-auto space-y-1">
                        {rated.length === 0 && <p className="text-[11px] text-content-muted italic">Aucun plat dans cette catégorie.</p>}
                        {rated.length > 0 && proposable.length === 0 && (
                            <p className="text-[11px] text-red-500 font-semibold">
                                Aucun plat de cette catégorie ne convient à son profil. Rendez un ingrédient retirable dans
                                votre carte, ou ajoutez un plat adapté.
                            </p>
                        )}
                        {proposable.map(({ dish, status, removals }) => {
                            const isSelected = selected.includes(dish.id);
                            return (
                                <label key={dish.id} className={`flex items-start gap-2 px-2 py-1.5 rounded-lg border text-xs cursor-pointer ${isSelected ? 'bg-brand-500/10 border-brand-500/40' : 'border-border hover:border-brand-500/30'}`}>
                                    <input type="checkbox" checked={isSelected} onChange={() => toggle(dish.id)} className="mt-0.5 w-3.5 h-3.5 rounded border-border accent-brand-600 shrink-0" />
                                    <span className="flex-1 min-w-0">
                                        <span className="font-semibold text-content block truncate">{dish.name}</span>
                                        {status === 'ok' ? (
                                            <span className="inline-flex items-center gap-1 mt-0.5 text-[9px] font-bold uppercase tracking-wide text-brand-600 dark:text-brand-400">
                                                <CheckCircle2 size={9} /> Convient tel quel
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-start gap-1 mt-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                                                <AlertTriangle size={9} className="mt-px shrink-0" />
                                                <span>Convient en retirant : {removals.map((r: any) => r.name).join(', ')}</span>
                                            </span>
                                        )}
                                    </span>
                                </label>
                            );
                        })}
                        {blocked.length > 0 && (
                            <div className="pt-1.5 mt-1.5 border-t border-border space-y-1">
                                <p className="text-[9px] font-bold uppercase tracking-wide text-content-muted">
                                    {blocked.length} plat(s) écarté(s) — incompatibles avec son profil
                                </p>
                                {blocked.map(({ dish, removals }) => (
                                    <div key={dish.id} className="px-2 py-1.5 rounded-lg border border-border/60 text-xs opacity-60">
                                        <span className="font-semibold text-content block truncate">{dish.name}</span>
                                        <span className="text-[9px] font-bold uppercase tracking-wide text-red-500">
                                            Contient {removals.filter((r: any) => !r.removable).map((r: any) => r.name).join(', ')} — non retirable
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {error && <p className="text-[11px] text-red-500 font-semibold">{error}</p>}
                    <Button onClick={send} disabled={sending || !selected.length} className="w-full">
                        <Send size={12} /> {sending ? 'Envoi...' : `Envoyer ${selected.length || ''} choix par email`}
                    </Button>
                </>
            )}
        </div>
    );
}

/** Détail d'un convive au clic sur sa ligne dans le tableau de suivi — son choix de menu par catégorie,
 *  ses restrictions et ses allergies. Demande explicite d'Emeni (2026-08-23) : "quand on clique sur un
 *  convive, on devrait voir ce qu'il a choisi en entrée plat dessert + allergie". */
export function GuestDetailModal({ eventId, guestId, onClose }: { eventId: string; guestId: string; onClose: () => void }) {
    const [detail, setDetail] = React.useState<GuestDetail | null>(null);
    /** Une catégorie mérite une proposition de plat dans deux cas : le convive a renoncé (« aucun plat ne me
     *  convenait ») ou il a explicitement demandé une alternative. Les deux étaient stockés séparément et seul
     *  le premier était affiché ici — d'où des fiches signalées en rouge dans la liste, mais sans aucun bouton
     *  une fois ouvertes. La demande explicite passe en premier : elle est plus urgente qu'un renoncement. */
    const categoriesNeedingDish = React.useMemo(() => {
        const requested = detail?.menu_alt_requested || [];
        const skipped = (detail?.menu_skipped_categories || []).filter((cat) => !requested.includes(cat));
        return [
            ...requested.map((category) => ({ category, requested: true })),
            ...skipped.map((category) => ({ category, requested: false })),
        ];
    }, [detail]);

    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [pickerCategory, setPickerCategory] = React.useState<string | null>(null);
    const [sentCategories, setSentCategories] = React.useState<string[]>([]);

    const load = React.useCallback(() => {
        moduleApi.getEvenementielGuestDetail(eventId, guestId)
            .then((d: any) => { if (d?.error) { setError(d.error); return; } setDetail(d); })
            .catch((e: any) => setError(e?.message || 'Erreur de chargement.'))
            .finally(() => setLoading(false));
    }, [eventId, guestId]);

    React.useEffect(() => { load(); }, [load]);

    return (
        <Modal open onClose={onClose} title={detail?.name || 'Convive'} icon={<Utensils size={16} />} maxWidth="max-w-md"
            footer={<Button variant="secondary" onClick={onClose}>Fermer</Button>}>
            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
            ) : error ? (
                <p className="text-xs text-red-500 font-semibold">{error}</p>
            ) : detail ? (
                <div className="space-y-4">
                    <div className="text-xs text-content-muted space-y-0.5">
                        <p>{detail.email}</p>
                        {detail.table_number && <p>Table {detail.table_number}</p>}
                        {!detail.responded_at && <p className="text-amber-600 dark:text-amber-400 font-semibold">N'a pas encore répondu</p>}
                    </div>

                    {Object.keys(detail.dishes_by_category).length > 0 ? (
                        <div className="space-y-1.5">
                            <h3 className="text-xs font-black text-content uppercase tracking-widest">Choix de menu</h3>
                            {Object.entries(detail.dishes_by_category).map(([category, dish]) => (
                                <p key={category} className="text-sm text-content"><span className="text-content-muted">{category} : </span><span className="font-bold">{dish}</span></p>
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-content-muted italic">Aucun choix de menu enregistré.</p>
                    )}

                    {categoriesNeedingDish.length > 0 && (
                        <div className="space-y-2">
                            <h3 className="text-xs font-black text-content uppercase tracking-widest">Catégories sans choix</h3>
                            {categoriesNeedingDish.map(({ category, requested }) => (
                                <div key={category} className="space-y-1.5">
                                    <div className={`flex items-center justify-between gap-2 border rounded-lg px-2.5 py-1.5 ${requested ? 'bg-red-500/10 border-red-500/25' : 'bg-amber-500/10 border-amber-500/20'}`}>
                                        <span className={`text-xs font-semibold ${requested ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                                            {requested ? `${category} — le convive DEMANDE une alternative` : `${category} — aucun plat ne convenait au convive`}
                                        </span>
                                        {pickerCategory !== category && !sentCategories.includes(category) && (
                                            <button onClick={() => setPickerCategory(category)} className="text-[10px] font-bold uppercase tracking-wide text-brand-600 dark:text-brand-400 hover:underline shrink-0">
                                                Proposer un plat
                                            </button>
                                        )}
                                        {sentCategories.includes(category) && (
                                            <span className="text-[10px] font-bold text-brand-600 dark:text-brand-400 shrink-0">Email envoyé</span>
                                        )}
                                    </div>
                                    {pickerCategory === category && (
                                        <AlternativeDishPicker
                                            eventId={eventId} guestId={guestId} category={category}
                                            allergies={detail.allergies} restrictions={detail.restrictions}
                                            onCancel={() => setPickerCategory(null)}
                                            onSent={() => { setPickerCategory(null); setSentCategories((prev) => [...prev, category]); load(); }}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <h3 className="text-xs font-black text-content uppercase tracking-widest">Restrictions</h3>
                        {detail.restrictions.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {detail.restrictions.map((r) => (
                                    <span key={r} className="text-[11px] font-semibold bg-brand-500/10 border border-brand-500/20 rounded-full px-2.5 py-1 text-brand-600 dark:text-brand-400">{r}</span>
                                ))}
                            </div>
                        ) : <p className="text-xs text-content-muted italic">Aucune.</p>}
                    </div>

                    <div className="space-y-1.5">
                        <h3 className="text-xs font-black text-content uppercase tracking-widest">Allergies</h3>
                        {detail.allergies.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {detail.allergies.map((a) => (
                                    <span key={a} className="text-[11px] font-bold bg-red-500/10 border border-red-500/20 rounded-full px-2.5 py-1 text-red-500">{a}</span>
                                ))}
                            </div>
                        ) : <p className="text-xs text-content-muted italic">Aucune.</p>}
                    </div>

                    {detail.medical_note && (
                        <div className="space-y-1.5">
                            <h3 className="text-xs font-black text-content uppercase tracking-widest">Maladie / grossesse</h3>
                            <p className="text-xs text-content bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5 whitespace-pre-wrap">{detail.medical_note}</p>
                        </div>
                    )}
                </div>
            ) : null}
        </Modal>
    );
}
